/**
 * Single error-envelope shape for every `/api/mobile/v1/*` response (API-07).
 *
 * Any non-2xx response from a mobile API route MUST conform to this shape so
 * the mobile fetch wrapper can parse it uniformly (NATIVE-06). New error
 * codes should be added here first, with a one-line comment explaining when
 * each code is emitted.
 */
import { z } from "zod";

/**
 * Closed set of machine-readable error codes the mobile client may receive.
 *
 * Add new codes ONLY when a new server-side condition needs to be acted on
 * specifically by the client (e.g. show a different UI). For one-off
 * messages, reuse `INTERNAL` or `VALIDATION_FAILED` and put the human
 * message in `error.message`.
 */
export const ERROR_CODES = [
  "UNAUTHORIZED",        // No or invalid auth token (HTTP 401)
  "FORBIDDEN",           // Authenticated but lacks permission for the resource (HTTP 403)
  "NOT_FOUND",           // Resource does not exist or is not owned by the caller (HTTP 404)
  "VALIDATION_FAILED",   // Request body failed Zod schema parse (HTTP 400)
  "RATE_LIMITED",        // Caller exceeded per-token / per-IP budget (HTTP 429)
  "MOBILE_API_DISABLED", // `mobile_api_enabled` global kill switch is off (HTTP 503)
  "CONTRACTOR_APP_DISABLED", // `contractor_app_enabled` global kill switch is off (HTTP 503)
  "FEATURE_DISABLED",    // A project/account-scoped feature is unavailable (HTTP 403/503)
  "WRITES_DISABLED",     // `mobile_writes_enabled` global kill switch is off — read still works (HTTP 503)
  // 409 for a TERMINAL conflict the caller must resolve (duplicate key,
  // "already checked in"); 503 for a RETRYABLE optimistic-lock / stale-resource
  // mismatch, because SyncManager's isTransientError is `status >= 500 || 429`
  // and would mark a 409 permanently failed. The status carries the retry
  // semantics; no consumer branches on this code.
  "CONFLICT",            // Duplicate key, optimistic-lock mismatch, or stale resource (HTTP 409/503)
  "INTERNAL",            // Unhandled server error — generic catch-all (HTTP 500)
  "PLAN_LOCKED",         // User's plan does not include the requested feature (HTTP 402/403)
  "READ_ONLY",           // Plan is in READ_ONLY_ACCOUNT mode (HTTP 403)
  "TRIAL_EXPIRED",       // User on TRIAL_EXPIRED plan state — gate writes until paid (HTTP 402)
  // 428 Precondition Required: a mobile worker sign-off on a PIN-required
  // project arrived without a valid `x-signoff-pin-proof` header (#1773/MP-29).
  // Terminal by design (not retried by the outbox): the app must run the
  // enrol/verify round trip first. The flow state (enrol / enter / locked)
  // rides in `details.status`.
  "PIN_REQUIRED",        // Sign-off needs PIN proof the request did not carry (HTTP 428)
  "UPLOAD_TOO_LARGE",    // Presigned upload commit exceeded declared size limit (HTTP 413)
  "UPLOAD_BAD_TYPE",     // Presigned upload content-type not in allow-list (HTTP 415)
  "SIGN_REQUEST_RETIRED", // Per-document /sign links are retired; sign via the project crew link (HTTP 410)
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export const ErrorCodeSchema = z.enum(ERROR_CODES);

export const ApiErrorEnvelope = z
  .object({
    error: z
      .object({
        code: ErrorCodeSchema.describe(
          "Machine-readable error code. See ERROR_CODES for the closed set.",
        ),
        message: z
          .string()
          .describe("Human-readable message safe to surface in mobile UI."),
        details: z
          .record(z.string(), z.unknown())
          .optional()
          .describe(
            "Optional structured context. Shape varies per code (e.g. Zod flatten for VALIDATION_FAILED).",
          ),
      })
      .strict(),
  })
  .strict();

export type ApiErrorEnvelope = z.infer<typeof ApiErrorEnvelope>;

/**
 * Server-side helper used by mobile API route handlers to build a uniform
 * error response payload. Kept in the contracts package so web + mobile
 * agree on the literal shape.
 */
export function buildErrorEnvelope(
  code: ErrorCode,
  message: string,
  details?: Record<string, unknown>,
): ApiErrorEnvelope {
  return details === undefined
    ? { error: { code, message } }
    : { error: { code, message, details } };
}

/**
 * Wire-tolerant envelope for CLIENT-side parsing (forward compatibility).
 *
 * Installed app bundles freeze a copy of `ERROR_CODES` at build time, but the
 * server keeps evolving: when a later deploy introduces a new code, an older
 * bundle validating with the closed `ErrorCodeSchema` rejects the WHOLE
 * envelope and degrades a typed error into a generic `PARSE` failure — losing
 * the status-appropriate handling plus the human-readable message. The client
 * therefore validates only the SHAPE: `code` is any non-empty string, unknown
 * sibling keys are stripped, and a `details` whose shape has drifted is
 * dropped rather than failing the envelope. `ApiErrorEnvelope` above stays
 * strict for server-side authoring and route-conformance tests. Narrow with
 * {@link isKnownErrorCode} where behaviour depends on a specific code.
 */
export const ApiErrorEnvelopeWire = z.object({
  error: z.object({
    code: z
      .string()
      .min(1)
      .describe(
        "Machine-readable error code. May be a code this bundle does not know yet.",
      ),
    message: z
      .string()
      .describe("Human-readable message safe to surface in mobile UI."),
    details: z
      .record(z.string(), z.unknown())
      .optional()
      .catch(undefined)
      .describe("Optional structured context; dropped when the shape drifts."),
  }),
});

export type ApiErrorEnvelopeWire = z.infer<typeof ApiErrorEnvelopeWire>;

/** Narrow a wire code to the closed set this bundle was built against. */
export function isKnownErrorCode(code: string): code is ErrorCode {
  return (ERROR_CODES as readonly string[]).includes(code);
}

/**
 * Client-side envelope parser. Returns the tolerant wire envelope (unknown
 * keys stripped, drifted `details` dropped) or `null` when the payload is not
 * an error envelope at all. Use after a non-2xx fetch:
 *
 *   const envelope = parseApiErrorEnvelope(await res.json());
 *   if (envelope) handle(envelope.error);
 */
export function parseApiErrorEnvelope(
  value: unknown,
): ApiErrorEnvelopeWire | null {
  const result = ApiErrorEnvelopeWire.safeParse(value);
  return result.success ? result.data : null;
}

/**
 * Client-side narrowing helper. Returns true if the parsed JSON conforms to
 * the WIRE error envelope shape (see {@link ApiErrorEnvelopeWire} — codes a
 * newer server added still count). Prefer {@link parseApiErrorEnvelope} when
 * you go on to read the envelope, so drifted fields are normalised for you.
 */
export function isApiErrorEnvelope(
  value: unknown,
): value is ApiErrorEnvelopeWire {
  return parseApiErrorEnvelope(value) !== null;
}
